import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, KeyRound, XCircle } from "lucide-react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import ErrorMessage from "../common/ErrorMessage";
import SuccessMessage from "../common/SuccessMessage";
import {
  isValidEmail,
  isValidFirstName,
  isValidPassword,
  isValidUsername,
} from "../../helpers/Users";

interface ManageUserProps {
  user: User;
  onUserUpdate: (user: User) => void;
  isAdmin: boolean;
  isMainAccount: boolean;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}
const ManageUser: React.FC<ManageUserProps> = ({
  user,
  onUserUpdate,
  isAdmin,
  isMainAccount,
  setError,
  setSuccess,
}) => {
  const [managementUser, setManagementUser] = useState<User>(user);
  const [erpClient, setErpClient] = useState<User | null>(null);
  const [erpUserReferenceSuggestions, setErpUserReferenceSuggestions] =
    useState<User[]>([]);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const checkUsernameAvailable = async (username: string) => {
    if (!managementUser.id && isValidUsername(username)) {
      api
        .get(
          API_URLS.USERS +
            `_actions/check_username_available?username=${username}`,
        )
        .then((response) => {
          setUsernameAvailable(response.data.available);
        });
    } else {
      setUsernameAvailable(true);
    }
  };

  const fetchSuggestClient = async (erpUserReference: string) => {
    if (erpUserReference && !managementUser.id) {
      try {
        const response = await api.get(
          API_URLS.USERS +
            `_actions/search_erp_client?client_id=${erpUserReference}`,
        );
        if (response.data.length > 0) {
          setErpUserReferenceSuggestions(response.data || []);
          setErpClient(null);
        }
        if (
          response.data.length == 1 &&
          response.data[0].erpUserReference.toLowerCase() ==
            erpUserReference.toLowerCase()
        ) {
          setErpClient(response.data[0] as User);
          if ((managementUser?.username ?? "") == "") {
            setManagementUser({
              ...managementUser,
              username: isAdmin ? response.data[0].erpUserReference : "", // Only set username if admin otherwise leave it empty
              type: "reseller",
              firstName: response.data[0].firstName || "",
              email: response.data[0].email || "",
              phoneNumber: response.data[0].phoneNumber || "",
              commercial_agent_portfolio:
                response.data[0].commercial_agent_portfolio || "",
            });
          }
        }
      } catch (error: any) {
        console.warn("Error fetching ERP user references:", error.message);
      }
    } else {
      setErpUserReferenceSuggestions([]);
    }
  };

  const handleDeviceLockToggle = async (
    deviceToken: string,
    locked: boolean,
  ) => {
    if (!isAdmin) {
      setError(
        "Apenas administradores podem bloquear/desbloquear dispositivos. Por favor, contacte o suporte.",
      );
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      const response = await api.post(
        API_URLS.USERS + `${managementUser?.id}/devices`,
        {
          token: deviceToken,
          locked: locked,
        },
      );
      if (!!response.data) {
        setSuccess(
          `Dispositivo ${locked ? "bloqueado" : "desbloqueado"} com sucesso!`,
        );
        setManagementUser(response.data as User);
        onUserUpdate(response.data as User); // Notify parent component of the update
      }
    } catch (error: any) {
      setError(
        `Erro ao ${locked ? "bloquear" : "desbloquear"} dispositivo: ${error.message}`,
      );
      console.warn("Error toggling device lock:", error.message);
    }
  };
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    const missingFields = [
      !managementUser.id && !managementUser.username && "Nome de utilizador",
      !managementUser.id && !managementUser.password && "Palavra-passe",
      !managementUser.id &&
        !managementUser.confirmPassword &&
        "Confirmação de Palavra-passe",
      !managementUser.firstName && "Primeiro nome",
      !managementUser.email && "Email",
      managementUser.type === "reseller" &&
        !managementUser.erpUserReference &&
        !erpClient?.id &&
        "Referência do cliente no ERP",
      !managementUser.type && "Tipo de utilizador",
    ].filter(Boolean);

    const validRulesFields = [
      !isValidFirstName(managementUser.firstName || "") && "Primeiro nome",
      !isValidEmail(managementUser.email || "") && "Email",
      !isValidUsername(managementUser.username || "") && "Nome de utilizador",
      !managementUser.id &&
        !isValidPassword(managementUser.password || "") &&
        "Palavra-passe",
      !managementUser.id &&
        managementUser.confirmPassword !== managementUser.password &&
        "Confirmação de Palavra-passe",
    ].filter(Boolean);

    if (missingFields.length > 0) {
      setError(
        `Os seguintes campos são obrigatórios: ${missingFields.join(", ")}`,
      );
      setLoading(false);
      return;
    }

    if (validRulesFields.length > 0) {
      setError(
        `Os seguintes campos não estão válidos: ${validRulesFields.join(", ")}`,
      );
      setLoading(false);
      return;
    }

    if (!managementUser.id && !usernameAvailable) {
      setError("O nome de utilizador já está em uso.");
      setLoading(false);
      return;
    }

    try {
      const response = await api.post(
        API_URLS.USERS + (managementUser.id ?? ""),
        managementUser,
      );
      if (response.data) {
        setManagementUser(response.data as User);
        onUserUpdate(response.data as User); // Notify parent component of the update
      }
    } catch (error: any) {
      setError(
        `Erro ao ${managementUser.id ? "atualizar" : "criar"} utilizador: ${error.message}`,
      );
      console.warn("Error submitting user data:", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (managementUser.erpUserReference != erpClient?.erpUserReference) {
      setErpClient(null);
    }
    fetchSuggestClient(managementUser.erpUserReference || "");
  }, [managementUser.erpUserReference]);

  useEffect(() => {
    checkUsernameAvailable(managementUser.username || "");
  }, [managementUser.username, managementUser.id]);

  useEffect(() => {
    if (managementUser.username == managementUser.erpUserReference) {
      setManagementUser({
        ...managementUser,
        type: "reseller",
        firstName: managementUser.firstName || erpClient?.firstName || "",
        email: managementUser.email || erpClient?.email || "",
        phoneNumber: managementUser.phoneNumber || erpClient?.phoneNumber || "",
        commercial_agent_portfolio:
          managementUser.commercial_agent_portfolio ||
          erpClient?.commercial_agent_portfolio ||
          "",
      });
    }
  }, [managementUser.username, managementUser.erpUserReference]);

  // @ts-ignore
  return (
    <>
      {loading ? (
        <div className="text-center">Loading...</div>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="container">
            <div className="box model-card">
              <div className="field is-horizontal">
                <div className="field-body">
                  <div className="field w-1/3 relative">
                    <label className="label" htmlFor="username">
                      Nome de Utilizador
                    </label>
                    <div className="control is-expanded">
                      <input
                        className={`input ${managementUser.id ? "is-static" : isValidUsername(managementUser.username) && usernameAvailable ? "" : "is-danger"}`}
                        type="text"
                        id="username"
                        name="username"
                        placeholder="Username"
                        readOnly={!!managementUser.id}
                        onChange={(e) => {
                          setManagementUser({
                            ...managementUser,
                            username: e.target.value,
                          });
                        }}
                        value={managementUser.username ?? ""}
                      />
                    </div>
                    {!managementUser.id &&
                      !isValidUsername(managementUser.username) && (
                        <p className="help is-danger">
                          O nome de utilizador deve ter pelo menos 3 caracteres
                          e conter apenas letras, números, pontos e underscores.
                        </p>
                      )}
                    {!managementUser.id && !usernameAvailable && (
                      <p className="help is-danger">
                        O nome de utilizador já está em uso.
                      </p>
                    )}
                  </div>
                  <div className="field w-2/3 relative">
                    <label className="label" htmlFor="erpUserReference">
                      Cliente Orimat #
                    </label>

                    <div className="control is-expanded">
                      <div className="field has-addons">
                        <input
                          className={`input ${managementUser.id || !isAdmin ? "is-static" : ""}`}
                          style={{ width: "25%" }}
                          type="text"
                          id="erpUserReference"
                          name="erpUserReference"
                          placeholder="Referência do cliente no ERP"
                          readOnly={!!managementUser.id || !isAdmin}
                          value={managementUser.erpUserReference ?? ""}
                          onChange={(e) =>
                            setManagementUser({
                              ...managementUser,
                              erpUserReference: e.target.value,
                            })
                          }
                        />
                        <p
                          className={`input is-static pl-5`}
                          style={{ width: "70%" }}
                        >
                          {erpClient?.firstName || managementUser.client?.name}
                        </p>
                      </div>
                      {!managementUser.id && isAdmin && (
                        <ul className="list-results is-hoverable absolute z-50 bg-cyan-100 w-[100%]">
                          {erpUserReferenceSuggestions &&
                            !erpClient?.firstName &&
                            erpUserReferenceSuggestions.map((suggestion) => (
                              <li
                                key={suggestion.erpUserReference}
                                className="list-item hover:bg-cyan-200"
                                onClick={() => {
                                  setErpClient(suggestion);
                                  setManagementUser({
                                    ...managementUser,
                                    erpUserReference:
                                      suggestion.erpUserReference,
                                    type: "reseller",
                                  });
                                }}
                              >
                                {suggestion.erpUserReference} -{" "}
                                {suggestion.firstName}
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {!managementUser.id && (
                <div className="field is-horizontal">
                  <div className="field-body">
                    <div className="field">
                      <label className="label" htmlFor="password">
                        Palavra-passe
                      </label>
                      <div className="control is-expanded">
                        <input
                          className={`input ${managementUser.id ? "is-hidden" : isValidPassword(managementUser.password) ? "" : "is-danger"}`}
                          type="password"
                          id="password"
                          name="password"
                          placeholder="********"
                          onChange={(e) =>
                            setManagementUser({
                              ...managementUser,
                              // @ts-ignore
                              password: e.target.value,
                            })
                          }
                        />
                      </div>
                      {!managementUser.id &&
                        !isValidPassword(managementUser.password) && (
                          <p className="help is-danger">
                            A palavra-passe deve ter pelo menos 6 caracteres.
                          </p>
                        )}
                    </div>
                    <div className="field">
                      <label className="label" htmlFor="confirmPassword">
                        Confira a Palavra-passe
                      </label>
                      <div className="control is-expanded">
                        <input
                          className={`input ${managementUser.id ? "is-hidden" : managementUser.confirmPassword !== managementUser.password ? "is-danger" : ""}`}
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          placeholder="********"
                          onChange={(e) =>
                            setManagementUser({
                              ...managementUser,
                              // @ts-ignore
                              confirmPassword: e.target.value,
                            })
                          }
                        />
                      </div>
                      {managementUser.confirmPassword !==
                        managementUser.password && (
                        <p className="help is-danger">
                          As palavras-passe não coincidem.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="field is-horizontal">
                <div className="field-body">
                  <div className="field">
                    <label className="label" htmlFor="firstName">
                      Primeiro Nome
                    </label>
                    <div className="control">
                      <input
                        className={`input ${isValidFirstName(managementUser.firstName ?? "") ? "" : "is-danger"}`}
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={managementUser.firstName ?? ""}
                        onChange={(e) =>
                          setManagementUser({
                            ...managementUser,
                            firstName: e.target.value,
                          })
                        }
                      />
                    </div>
                    {!isValidFirstName(managementUser.firstName ?? "") && (
                      <p className="help is-danger">
                        O primeiro nome é obrigatório.
                      </p>
                    )}
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="lastName">
                      Ultimo Nome
                    </label>
                    <div className="control">
                      <input
                        className="input"
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={managementUser.lastName ?? ""}
                        onChange={(e) =>
                          setManagementUser({
                            ...managementUser,
                            lastName: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="field is-horizontal">
                <div className="field-body">
                  <div className="field">
                    <label className="label" htmlFor="email">
                      Email
                    </label>
                    <div className="control">
                      <input
                        className={`input ${isValidEmail(managementUser.email ?? "") ? "" : "is-danger"}`}
                        type="email"
                        id="email"
                        name="email"
                        value={managementUser.email ?? ""}
                        onChange={(e) =>
                          setManagementUser({
                            ...managementUser,
                            email: e.target.value,
                          })
                        }
                      />
                    </div>
                    {!isValidEmail(managementUser.email ?? "") && (
                      <p className="help is-danger">
                        O email é obrigatório e deve ser válido.
                      </p>
                    )}
                  </div>

                  <div className="field">
                    <label className="label" htmlFor="phone">
                      Telefone
                    </label>
                    <div className="control">
                      <input
                        className="input"
                        type="tel"
                        id="phone"
                        name="phone"
                        value={managementUser.phoneNumber ?? ""}
                        onChange={(e) =>
                          setManagementUser({
                            ...managementUser,
                            phoneNumber: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="box model-card">
              <div className="field is-horizontal">
                <div className="field-body">
                  <div className="field">
                    <label className="label" htmlFor="type">
                      Tipo de Utilizador
                    </label>
                    <div className="control is-expanded">
                      <select
                        className={`select ${managementUser.type ? "" : "is-danger"}`}
                        id="type"
                        name="type"
                        value={managementUser.type}
                        onChange={(e) =>
                          setManagementUser({
                            ...managementUser,
                            type: e.target.value as
                              | "guest"
                              | "user"
                              | "reseller"
                              | "commercial"
                              | "admin",
                          })
                        }
                      >
                        <option value="guest" disabled={true}>
                          Anónimo
                        </option>
                        {(isMainAccount || isAdmin) && (
                          <>
                            <option value="user">Site / Self-Service</option>
                            <option value="reseller">Revendedor</option>
                          </>
                        )}
                        {isAdmin && (
                          <>
                            <option value="commercial">Comercial</option>
                            <option value="admin">Administrador</option>
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="field">
                      <label className="label" htmlFor="client">
                        Carteira de Comercial
                      </label>
                      <div className="control is-expanded">
                        <input
                          type="text"
                          className={`input`}
                          id="client"
                          name="client"
                          value={
                            managementUser.commercial_agent_portfolio ||
                            erpClient?.commercial_agent_ref ||
                            ""
                          }
                          onChange={(e) =>
                            setManagementUser({
                              ...managementUser,
                              commercial_agent_portfolio: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                  {isAdmin && (
                    <div className="field">
                      <label className="label" htmlFor="locked">
                        Subcontas
                      </label>
                      <div className="control is-expanded">
                        <input
                          type="number"
                          className="input"
                          id="number_subaccounts_allowed"
                          name="number_subaccounts_allowed"
                          min="0"
                          max="10"
                          disabled={
                            managementUser.type != "reseller" ||
                            !isValidUsername(managementUser.username) ||
                            managementUser.username?.toLowerCase() !=
                              managementUser.erpUserReference?.toLowerCase()
                          }
                          value={managementUser.number_subaccounts_allowed || 0}
                          onChange={(e) =>
                            setManagementUser({
                              ...managementUser,
                              number_subaccounts_allowed: parseInt(
                                e.target.value ?? 0,
                                10,
                              ),
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="field is-horizontal">
                <div className="field-body">
                  <div className="field">
                    <label className="label" htmlFor="isCredentialsExpired">
                      Password Expirada
                    </label>
                    <div className="control is-expanded">
                      <input
                        type="checkbox"
                        className="checkbox"
                        id="isCredentialsExpired"
                        name="isCredentialsExpired"
                        disabled={!managementUser.id}
                        readOnly={!managementUser.id}
                        checked={managementUser.credentialsExpired ?? false}
                        onChange={(e) =>
                          setManagementUser({
                            ...managementUser,
                            credentialsExpired: e.target.checked,
                          })
                        }
                      />
                      <label htmlFor={"isCredentialsExpired"} className="ml-2">
                        Forçar troca de senha no próximo login
                      </label>
                    </div>
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="isEnabled">
                      Ativar/Desativar usuário
                    </label>
                    <div className="control is-expanded">
                      <input
                        type="checkbox"
                        className="checkbox"
                        id="isEnabled"
                        name="isEnabled"
                        checked={managementUser.enabled ?? true}
                        onChange={(e) =>
                          setManagementUser({
                            ...managementUser,
                            enabled: e.target.checked,
                          })
                        }
                      />
                      <label htmlFor={"isEnabled"} className="ml-2">
                        Ativo
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {managementUser.id && (
              <div className="box model-card">
                <p className="subtitle is-4">Dispositivos</p>
                <table className="table is-fullwidth">
                  <thead>
                    <tr>
                      <th>Dispositivo</th>
                      <th className="w-1/6">Endereço de IP</th>
                      <th className="w-2/6">Ultimo Login</th>
                      <th className="actions w-60px"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {managementUser.userDevice &&
                    managementUser.userDevice.length > 0 ? (
                      managementUser.userDevice?.map((device) => (
                        <tr key={device.token}>
                          <td className="text-ellipsis">
                            <span
                              className="font-mono"
                              title={device.user_agent}
                            >
                              {device.token.split(".")[0]}
                            </span>
                          </td>
                          <td className="text-ellipsis">
                            <span
                              className="font-mono"
                              title={device.ip_address}
                            >
                              {device.ip_address}
                            </span>
                          </td>
                          <td className="font-mono">
                            {device.last_login_at
                              ? new Date(device.last_login_at).toLocaleString()
                              : "Never"}
                          </td>
                          <td>
                            <Link
                              to={`#`}
                              onClick={(e) => {
                                handleDeviceLockToggle(
                                  device.token,
                                  !device.locked,
                                );
                                e.preventDefault();
                              }}
                            >
                              <span className="icon is-medium has-text-primary">
                                {device.locked ? (
                                  <XCircle
                                    size={24}
                                    className="has-text-danger"
                                  />
                                ) : (
                                  <CheckCircle
                                    size={24}
                                    className="has-text-success"
                                  />
                                )}
                              </span>
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="has-text-centered">
                          Não existe dispositivos / Não foram feitos acessos.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="field">
              <div className="control">
                <button type="submit" className="button is-primary">
                  {managementUser.id ? "Altera utilizador" : "Cria utilizador"}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </>
  );
};

export default ManageUser;
